import styles from "./SurveyFooter.module.scss";

import { useTranslation } from "react-i18next";

import { AppLoadingIndicator } from "@/modules/shared/components/AppLoadingIndicator/AppLoadingIndicator";
import { Copyright } from "@/modules/shared/components/Copyright";
import { ArrowIcon } from "@/modules/shared/svgs/ArrowIcon";

interface SurveyFooterProps {
  currentStep?: number;
  totalSteps?: number;
  canGoBack?: boolean;
  onBack?: () => void;
  canGoForward?: boolean;
  onForward?: () => void;
  isForwardSelected?: boolean;
  isLastStep?: boolean;
  canSubmit?: boolean;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  hasError?: boolean;
  hasOverflow?: boolean;
}

export function SurveyFooter({
  currentStep,
  totalSteps,
  canGoBack = false,
  onBack,
  canGoForward = false,
  onForward,
  isForwardSelected = false,
  isLastStep = false,
  canSubmit = true,
  onSubmit,
  isSubmitting = false,
  hasError = false,
  hasOverflow = false,
}: SurveyFooterProps) {
  const { t } = useTranslation();

  const hasNavigation = currentStep !== undefined && totalSteps !== undefined;

  return (
    <footer className={styles.footer} data-overflow={hasOverflow}>
      {hasNavigation ? (
        <div className={styles.navBlock}>
          {hasError ? (
            <p className={styles.error} role="alert">
              {t("sessionSurvey.footer.submitError")}
            </p>
          ) : null}

          <nav className={styles.navigation}>
            <button
              type="button"
              className={`${styles.navButton} ${styles.back}`}
              onClick={onBack}
              disabled={!canGoBack}
              aria-label={t("sessionSurvey.footer.back")}
            >
              <ArrowIcon color="currentColor" />
            </button>

            <span className={styles.counter}>
              {t("sessionSurvey.footer.pageCounter", {
                current: currentStep,
                total: totalSteps,
              })}
            </span>

            {isLastStep ? (
              <button
                type="button"
                className={styles.submit}
                onClick={onSubmit}
                disabled={!canSubmit || isSubmitting}
                data-selected={isForwardSelected}
                aria-busy={isSubmitting}
                aria-label={t("sessionSurvey.footer.submit")}
              >
                {isSubmitting ? (
                  <AppLoadingIndicator size="small" color="currentColor" />
                ) : (
                  t("sessionSurvey.footer.submit")
                )}
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.navButton} ${styles.forward}`}
                onClick={onForward}
                disabled={!canGoForward}
                data-selected={isForwardSelected}
                aria-label={t("sessionSurvey.footer.next")}
              >
                <ArrowIcon color="currentColor" />
              </button>
            )}
          </nav>
        </div>
      ) : null}

      <Copyright className={styles.copyright} />
    </footer>
  );
}
